import { Database } from "lucide-react";
import type { Team } from "@/lib/api/types";
import { useSetAutoApplyMigrations, useTeamMembers } from "@/hooks/queries/use-teams";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useAuthStore } from "@/stores/auth-store";

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

/**
 * Project setting: apply database migrations automatically when a change ships
 * (ENG-421, server side ENG-419). Same shape and admin rule as the speed card.
 * Project admins, organization admins and platform admins can change it;
 * everyone else sees it read-only.
 */
export function AutoApplyMigrationsCard({ team }: { team: Team }) {
  const me = useAuthStore((s) => s.user);
  const { data: onboarding } = useOnboardingState();
  const { data: members } = useTeamMembers(team.id);
  const setAutoApply = useSetAutoApplyMigrations(team.id);

  const current = setAutoApply.data?.data.autoApplyMigrations ?? team.autoApplyMigrations ?? false;
  const checked = setAutoApply.isPending ? setAutoApply.variables : current;
  const myRole = members?.find((m) => m.user.id === me?.id)?.role;
  const canEdit =
    !!me?.isGlobalAdmin ||
    (onboarding?.step === "ready" && onboarding.organizationRole === "admin") ||
    myRole === "admin";

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-medium">
          <Database className="h-4 w-4" />
          Database changes
        </h3>
        <p className="text-sm text-muted-foreground">
          How database changes in this project&apos;s pull requests reach the live database.
        </p>
      </div>

      <label className="flex items-start gap-3 rounded-lg border border-white/[0.08] p-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={checked}
          disabled={!canEdit || setAutoApply.isPending}
          onChange={(e) => { setAutoApply.mutate(e.target.checked); }}
        />
        <span>
          <span className="block font-medium">Apply database changes automatically</span>
          <span className="block text-xs text-muted-foreground">
            When a change ships, the database is backed up and its migrations are applied, with no manual steps. Changes that can lose data still need a project owner&apos;s approval before merging.
          </span>
        </span>
      </label>

      {!canEdit ? (
        <p className="text-xs text-muted-foreground">Only a project admin can change this.</p>
      ) : null}
      {errorText(setAutoApply.error) ? (
        <p className="text-xs text-destructive">{errorText(setAutoApply.error)}</p>
      ) : null}
    </div>
  );
}
